import { createReducer } from '@reduxjs/toolkit';
import { normalize } from 'normalizr';
import mergeWith from 'lodash/mergeWith';
import {
  createAiCharacterObjection,
  createAiLearnerGoal,
  deleteAiCharacterObjection,
  deleteAiLearnerGoal,
  fetchAiRolePlay,
  getAiRolePlayConversation,
  getAiRolePlaySubmission,
  getAiRolePlaySubmissionsList,
  setAiRolePlaySubmissionCompleted,
  setAiRolePlaySubmissionClosingReason,
  setAiRolePlaySubmissionPlaybackReady,
  setAiRolePlaySubmissionPendingPointsModal,
  clearAiRolePlaySubmissionPendingPointsModal,
  getAiRolePlayAvailableFaces,
  getAiRolePlayAvailableVoiceOptions,
} from 'redux/actions/ai-role-play';
import { AiRolePlaySubmission } from 'redux/schemas/models/ai-role-play';
import { AiRolePlaySchema } from 'redux/schemas/api/lecture-components';
import { ClosingReason } from 'lecture_pages/components/ai-role-play/ai-conversation-modal/stream-ai-response';
import { replaceArrays } from 'shared/lodash-utils';
import { initialRootState } from '.';

/**
 * Creates a default submission object with null fields.
 * Used as a fallback when a submission doesn't exist in state yet.
 */
const createEmptySubmission = (
  id: number,
  aiRolePlayId: number,
  overrides: Partial<{
    finishedAt: string | null;
    closingReason: ClosingReason | null;
  }> = {},
) => ({
  id,
  finishedAt: null,
  closingReason: null,
  status: null,
  attemptNumber: null,
  createdAt: null,
  updatedAt: null,
  openingNarrative: null,
  leaderboardRank: null,
  leaderboardPoints: null,
  priorLeaderboardRank: null,
  feedback: null,
  aiRolePlay: { id: aiRolePlayId },
  ...overrides,
});

/**
 * Updates pointsReceived only if the new value is greater than the existing one.
 * This prevents failed retries (which return 0) from overwriting earned points.
 */
const updatePointsIfHigher = (
  aiRolePlay: { pointsReceived: number | null },
  newPoints: number | null,
) => {
  if ((newPoints ?? 0) > (aiRolePlay.pointsReceived ?? 0)) {
    aiRolePlay.pointsReceived = newPoints;
  }
};

export default createReducer(initialRootState, builder => {
  builder
    .addCase(createAiCharacterObjection.fulfilled, (state, action) => {
      const { aiRolePlayId, id, text } = action.payload;
      const aiRolePlay = state.models.aiRolePlays[aiRolePlayId];
      if (aiRolePlay) {
        aiRolePlay.aiCharacterObjections = [
          ...aiRolePlay.aiCharacterObjections,
          { id, text },
        ];
      }
    })
    .addCase(deleteAiCharacterObjection.fulfilled, (state, action) => {
      const { aiCharacterObjectionId, aiRolePlayId } = action.meta.arg;
      const aiRolePlay = state.models.aiRolePlays[aiRolePlayId];
      if (aiRolePlay) {
        aiRolePlay.aiCharacterObjections = aiRolePlay.aiCharacterObjections.filter(
          objection => objection.id !== aiCharacterObjectionId,
        );
      }
    })
    .addCase(createAiLearnerGoal.fulfilled, (state, action) => {
      const { aiRolePlayId, id, text } = action.payload;
      const aiRolePlay = state.models.aiRolePlays[aiRolePlayId];
      if (aiRolePlay) {
        aiRolePlay.aiLearnerGoals = [
          ...aiRolePlay.aiLearnerGoals,
          { id, text },
        ];
      }
    })
    .addCase(deleteAiLearnerGoal.fulfilled, (state, action) => {
      const { aiLearnerGoalId, aiRolePlayId } = action.meta.arg;
      const aiRolePlay = state.models.aiRolePlays[aiRolePlayId];
      if (aiRolePlay) {
        aiRolePlay.aiLearnerGoals = aiRolePlay.aiLearnerGoals.filter(
          goal => goal.id !== aiLearnerGoalId,
        );
      }
    })
    .addCase(getAiRolePlaySubmission.fulfilled, (state, action) => {
      const submission = action.payload.result;
      const aiRolePlay = state.models.aiRolePlays[submission.aiRolePlay.id];
      if (aiRolePlay) {
        state.models.aiRolePlaySubmissions[submission.id] = { ...state.models.aiRolePlaySubmissions[submission.id], ...submission };
      }
    })
    .addCase(setAiRolePlaySubmissionCompleted, (state, action) => {
      const { aiRolePlayId, aiRolePlaySubmissionId, pointsReceived, finishedAt } = action.payload;
      const aiRolePlay = state.models.aiRolePlays[aiRolePlayId];
      if (aiRolePlay) {
        aiRolePlay.submissionId = aiRolePlaySubmissionId;
        updatePointsIfHigher(aiRolePlay, pointsReceived);
        aiRolePlay.progress = 'completed';
      }
      const submission = state.models.aiRolePlaySubmissions[aiRolePlaySubmissionId];
      if (submission) {
        submission.finishedAt = finishedAt;
      } else {
        state.models.aiRolePlaySubmissions[aiRolePlaySubmissionId] = createEmptySubmission(
          aiRolePlaySubmissionId,
          aiRolePlayId,
          { finishedAt },
        );
      }

      // Prepend the new submission into the cached user-submissions list so the
      // dropdown reflects it immediately without re-fetching. The Pusher event is
      // delivered on the user-scoped channel, so the submitter is the current user.
      // Only touch an already-loaded entry — if it doesn't exist, the next mount
      // will fetch.
      const { currentUserId } = state.app;
      const userListState = currentUserId
        ? state.app.aiRolePlay.userSubmissions?.[aiRolePlayId]?.[currentUserId]
        : undefined;
      if (userListState && !userListState.submissionIds.includes(aiRolePlaySubmissionId)) {
        userListState.submissionIds = [aiRolePlaySubmissionId, ...userListState.submissionIds];
        if (typeof userListState.resultsCount === 'number') {
          userListState.resultsCount += 1;
        }
      }
    })
    .addCase(setAiRolePlaySubmissionClosingReason, (state, action) => {
      const { aiRolePlaySubmissionId, closingReason, aiRolePlayId } = action.payload;
      const aiRolePlay = state.models.aiRolePlays[aiRolePlayId];
      if (aiRolePlay) {
        aiRolePlay.submissionId = aiRolePlaySubmissionId;
      }
      const submission = state.models.aiRolePlaySubmissions[aiRolePlaySubmissionId];
      if (submission) {
        submission.closingReason = closingReason;
      } else {
        state.models.aiRolePlaySubmissions[aiRolePlaySubmissionId] = createEmptySubmission(
          aiRolePlaySubmissionId,
          aiRolePlayId,
          { closingReason },
        );
      }
    })
    .addCase(setAiRolePlaySubmissionPlaybackReady, (state, action) => {
      const { aiRolePlaySubmissionId, mergedPlaybackUrl } = action.payload;
      const submission = state.models.aiRolePlaySubmissions[aiRolePlaySubmissionId];
      if (submission) {
        submission.mergedPlaybackUrl = mergedPlaybackUrl;
      }
    })
    .addCase(setAiRolePlaySubmissionPendingPointsModal, (state, action) => {
      const { aiRolePlaySubmissionId, ...pendingPointsModal } = action.payload;
      state.app.aiRolePlay.pendingPointsModal[aiRolePlaySubmissionId] = pendingPointsModal;
    })
    .addCase(clearAiRolePlaySubmissionPendingPointsModal, (state, action) => {
      delete state.app.aiRolePlay.pendingPointsModal[action.payload.aiRolePlaySubmissionId];
    })
    .addCase(getAiRolePlaySubmissionsList.pending, (state, action) => {
      const { aiRolePlayId, page = 1, userId } = action.meta.arg;
      if (userId != null) {
        if (!state.app.aiRolePlay.userSubmissions[aiRolePlayId]) {
          state.app.aiRolePlay.userSubmissions[aiRolePlayId] = {};
        }
        const userSubmissions = state.app.aiRolePlay.userSubmissions[aiRolePlayId];
        if (userSubmissions[userId]) {
          userSubmissions[userId].isLoading = true;
          if (page === 1) userSubmissions[userId].isOnceLoaded = false;
        } else {
          userSubmissions[userId] = { submissionIds: [], isOnceLoaded: false, isLoading: true, hasMore: true };
        }
      } else {
        const galleryState = state.app.aiRolePlay.gallerySubmissions[aiRolePlayId];
        if (galleryState) {
          galleryState.isLoading = true;
          if (page === 1) galleryState.isOnceLoaded = false;
        } else {
          state.app.aiRolePlay.gallerySubmissions[aiRolePlayId] = {
            submissionIds: [],
            isOnceLoaded: false,
            isLoading: true,
            hasMore: true,
          };
        }
      }
    })
    .addCase(getAiRolePlaySubmissionsList.fulfilled, (state, action) => {
      const { aiRolePlayId, page = 1, userId } = action.meta.arg;
      const submissions: AiRolePlaySubmission[] = action.payload?.result?.submissions ?? [];
      const resultsCount: number = action.payload?.result?.resultsCount ?? 0;
      const perPage: number = action.payload?.result?.perPage ?? 10;
      const feedbackEvaluationCounts = action.payload?.result?.feedbackEvaluationCounts;

      // Normalize each submission into the model store, preserving any
      // pre-existing fields (e.g. messages loaded by getAiRolePlayConversation).
      submissions.forEach((submission) => {
        state.models.aiRolePlaySubmissions[submission.id] = {
          ...state.models.aiRolePlaySubmissions[submission.id],
          ...submission,
        };
      });

      const ids = submissions.map((s) => s.id);

      if (userId != null) {
        if (!state.app.aiRolePlay.userSubmissions[aiRolePlayId]) {
          state.app.aiRolePlay.userSubmissions[aiRolePlayId] = {};
        }
        const userSubmissions = state.app.aiRolePlay.userSubmissions[aiRolePlayId];
        if (!userSubmissions[userId]) {
          userSubmissions[userId] = { submissionIds: [], isOnceLoaded: false, isLoading: false, hasMore: true };
        }
        const listState = userSubmissions[userId];
        listState.submissionIds = page === 1
          ? ids
          : Array.from(new Set([...listState.submissionIds, ...ids]));
        listState.isOnceLoaded = true;
        listState.isLoading = false;
        listState.hasMore = page * perPage < resultsCount;
        listState.resultsCount = resultsCount;
      } else {
        // Pending should have created the galleryState; create it defensively if not.
        if (!state.app.aiRolePlay.gallerySubmissions[aiRolePlayId]) {
          state.app.aiRolePlay.gallerySubmissions[aiRolePlayId] = {
            submissionIds: [],
            isOnceLoaded: false,
            isLoading: false,
            hasMore: true,
          };
        }

        const galleryState = state.app.aiRolePlay.gallerySubmissions[aiRolePlayId];
        galleryState.submissionIds = page === 1
          ? ids
          : Array.from(new Set([...galleryState.submissionIds, ...ids]));
        galleryState.isOnceLoaded = true;
        galleryState.isLoading = false;
        galleryState.hasMore = page * perPage < resultsCount;
        galleryState.resultsCount = resultsCount;
        if (feedbackEvaluationCounts) {
          galleryState.feedbackEvaluationCounts = feedbackEvaluationCounts;
        }
      }
    })
    .addCase(getAiRolePlaySubmissionsList.rejected, (state, action) => {
      if (action.meta.aborted) return;
      const { aiRolePlayId, userId } = action.meta.arg;
      if (userId != null) {
        const listState = state.app.aiRolePlay.userSubmissions?.[aiRolePlayId]?.[userId];
        if (listState) {
          listState.isLoading = false;
          listState.isOnceLoaded = true;
        }
      } else {
        const galleryState = state.app.aiRolePlay.gallerySubmissions[aiRolePlayId];
        if (galleryState) {
          galleryState.isLoading = false;
          galleryState.isOnceLoaded = true;
        }
      }
    })
    .addCase(getAiRolePlayConversation.fulfilled, (state, action) => {
      const { submissionId } = action.meta.arg;
      const { chatMessages = [] } = (action.payload as { chatMessages?: any[] }) || {};
      state.app.aiRolePlay.conversationMessages[submissionId] = chatMessages;
    })
    .addCase(getAiRolePlayAvailableFaces.fulfilled, (state, action) => {
      state.models.aiRolePlayAvailableFacePresets = action.payload.availableFacePresets;
    })
    .addCase(getAiRolePlayAvailableVoiceOptions.fulfilled, (state, action) => {
      state.models.aiRolePlayAvailableVoiceOptions = action.payload;
    })
    .addCase(fetchAiRolePlay.fulfilled, (state, action) => {
      if (action.payload?.id) {
        const normalized = normalize(action.payload, AiRolePlaySchema);
        mergeWith(state.models, normalized.entities, replaceArrays);
      }
    });
});
